import { useState, useRef, useEffect } from "react";
import { Stack, TextInput, Button, Alert, Text, Group, Anchor } from "@mantine/core";
import { apiJson, ApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

const EMAIL_REGEX = /^[^\s@]{1,64}@[^\s@]{1,255}$/;

const RESEND_THROTTLE_MS = 30_000;

type Phase = "idle" | "sending" | "sent" | "error";

interface SendResponse {
  ok: true;
}

function mapError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return "Please enter a valid email address.";
    if (err.status === 429) return "Too many attempts. Please try again in a few minutes.";
    if (err.status >= 500) return "Something went wrong. Please try again or contact support.";
    return err.message || "Something went wrong. Please try again or contact support.";
  }
  if (err instanceof TypeError) {
    return "Network error. Please check your connection and try again.";
  }
  return "Something went wrong. Please try again or contact support.";
}

export default function MagicLinkLogin() {
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState(0);
  const lastSentEmailRef = useRef<string>("");
  const { clearAuthError } = useAuth();

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const send = async (target: string) => {
    setPhase("sending");
    setErrorMessage(null);
    // Requesting a link is an explicit new sign-in action — the one moment a
    // previous failure may be cleared (AuthContext keeps it sticky otherwise).
    clearAuthError();
    try {
      await apiJson<SendResponse>("/auth/magic-link/send", {
        method: "POST",
        body: JSON.stringify({ email: target }),
        unwrapData: false,
      });
      // Persist email for the callback flow's device-binding check.
      // Safari ITP and private-browsing can throw on setItem; the callback
      // handles a missing entry by prompting the user for their email,
      // so swallowing the failure is safe.
      try {
        window.localStorage.setItem("emailForSignIn", target);
      } catch {
        /* no-op */
      }
      lastSentEmailRef.current = target;
      setPhase("sent");
      setResendCountdown(RESEND_THROTTLE_MS / 1000);
    } catch (err) {
      setErrorMessage(mapError(err));
      setPhase("error");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setErrorMessage("Please enter a valid email address.");
      setPhase("error");
      return;
    }
    void send(trimmed);
  };

  const handleResend = () => {
    if (resendCountdown > 0 || !lastSentEmailRef.current) return;
    void send(lastSentEmailRef.current);
  };

  if (phase === "sent") {
    return (
      <Stack
        gap="sm"
        role="status"
        aria-live="polite"
        style={{ color: "rgba(220, 220, 220, 1)" }}
      >
        <Text size="sm" fw={500}>
          Check your email — we sent a sign-in link to{" "}
          <Text component="span" fw={700}>
            {lastSentEmailRef.current}
          </Text>
          .
        </Text>
        {/* Generic on purpose, and it must stay that way: /auth/magic-link/send
            answers identically for every address, so a partner name, a domain,
            or any dependence on the address typed would turn this line into an
            oracle for whether an account exists. */}
        <Text size="xs" c="dimmed">
          If nothing arrives, your workspace may have moved — sign in at your
          partner's address.
        </Text>
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            Didn't receive it?
          </Text>
          <Anchor
            component="button"
            type="button"
            size="xs"
            onClick={handleResend}
            disabled={resendCountdown > 0}
            aria-disabled={resendCountdown > 0}
          >
            {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Resend"}
          </Anchor>
        </Group>
      </Stack>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Stack gap="sm">
        <TextInput
          type="email"
          label="Email"
          placeholder="you@company.com"
          value={email}
          onChange={(event) => {
            setEmail(event.currentTarget.value);
            if (phase === "error") {
              setPhase("idle");
              setErrorMessage(null);
            }
          }}
          required
          autoComplete="email"
          radius="md"
          styles={{
            label: { color: "rgba(188, 188, 188, 1)", fontWeight: 500 },
          }}
        />
        {phase === "error" && errorMessage && (
          <Alert
            color="red"
            variant="light"
            role="alert"
            styles={{
              root: {
                backgroundColor: "rgba(220, 53, 69, 0.18)",
                borderColor: "rgba(255, 120, 120, 0.4)",
              },
              message: { color: "rgba(255, 220, 220, 1)" },
              icon: { color: "rgba(255, 180, 180, 1)" },
            }}
          >
            {errorMessage}
          </Alert>
        )}
        <Button
          type="submit"
          fullWidth
          size="md"
          radius="md"
          color="dark"
          loading={phase === "sending"}
          style={{ backgroundColor: "rgba(45, 45, 45, 1)" }}
        >
          Send sign-in link
        </Button>
      </Stack>
    </form>
  );
}
